import { Stack } from '@ltvco/refresh-lib/theme';
import {
  CHATAI_LOADING_STATUS,
  Message,
  MessageBoxContainer,
} from '@ltvco/refresh-lib/v2';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  MessagesContentStack,
  GenericReportResponse,
} from '@ltvco/refresh-lib/v2';
import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageContent } from './MessageContent/MessageContent';
import { baseMessageBoxContainerStyles } from '../components/ChatPage/components/MessagesContainer';
import { DamageAnalyzerContent } from './DamageAnalyzerContent';
import { AssistantSection } from './AssistantSection';
import { RetrySearchInstruction } from './RetrySearchInstruction';
import {
  findScrollContainer,
  findHeaderElement,
  calculateScrollPosition,
  calculateStickyIndex,
  calculateTailGap,
} from './utils';

export interface LoadingStatus {
  message: string;
  subtitle: string;
}

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onSendMessage?: (message?: string, imageBase64?: string) => void;
};

interface MessagePair {
  user: Message;
  assistant: Message | null;
  userIndex: number;
  assistantIndex: number | null;
}

interface DamageAnalyzerEstimate {
  condition_impact_percent?: number;
  damage_category?: string;
  estimated_cost_labor_max?: number;
  estimated_cost_labor_min?: number;
  estimated_cost_parts_max?: number;
  estimated_cost_parts_min?: number;
  estimated_cost_total_max?: number;
  estimated_cost_total_min?: number;
  justification?: string;
  net_gain_after_repair?: number;
  post_repair_value_change?: number;
  repair_recommended?: boolean;
  repair_type?: string;
  severity?: string;
}

type MessageWithPhotoDamage = Message & {
  photoDamageEstimate?: DamageAnalyzerEstimate;
};

export interface MessageReportData {
  reportData: GenericReportResponse;
  type: string;
  permalink: string;
}

const MAX_TAIL_GAP = 80;

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onSendMessage,
}: MessagesContainerProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = useScreenSize();
  const [lastAutoOpenedMessageContent, setLastAutoOpenedMessageContent] =
    useState<string | null>(null);
  const [imageToAnalyze, setImageToAnalyze] = useState<string | null>(null);
  const [hasImageError, setHasImageError] = useState(false);

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const pairRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [tailGap, setTailGap] = useState(0);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);
  const visibleAssistantsRef = useRef<Set<number>>(new Set());

  const [stickyIndex, setStickyIndex] = useState<number | null>(null);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const handleClearImage = () => {
    setImageToAnalyze(null);
    setHasImageError(false);
  };

  const handleSetImageToAnalyze = (image: string) => {
    setImageToAnalyze(image);
  };

  // Reset local image/error state when the chat conversation is cleared
  useEffect(() => {
    if (messages.length === 0) {
      setImageToAnalyze(null);
      setHasImageError(false);
    }
  }, [messages.length]);

  // Check for image analysis error in assistant messages
  // Error occurs when photoDamageEstimate contains an error field
  useEffect(() => {
    if (messages.length > 0) {
      const lastAssistantMessage = messages.findLast(
        (msg) => msg.role === 'assistant',
      );

      if (lastAssistantMessage?.photoDamageEstimate?.error !== undefined) {
        setHasImageError(true);
      }
    }
  }, [messages]);
  // state for smooth scroll / anti-loop
  const lastMeasuredScrollHeight = useRef<number>(0); // detects real content growth
  const prevPairsCountRef = useRef<number>(0); // reliable detection of "new pair"

  // State/refs for smooth scroll
  const smoothScrollRef = useRef<{
    requestAnimationFrameId: number | null;
    target: number | null;
  }>({
    requestAnimationFrameId: null,
    target: null,
  });

  useEffect(() => {
    if (messages.length > 0 && !isMobile) {
      const lastMessage = messages[messages.length - 1];
      // Only auto-open if last message is an assistant report and it's a new one
      if (
        lastMessage.role === 'assistant' &&
        lastMessage.reports?.[0]?.permalink &&
        lastMessage.content !== lastAutoOpenedMessageContent
      ) {
        // Small delay to ensure the message is fully rendered
        const timer = setTimeout(() => {
          setLastAutoOpenedMessageContent(lastMessage.content || null);
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [messages, lastAutoOpenedMessageContent]);

  // Cancel animation if the user interacts
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) {
      return;
    }

    const cancelIfAnimating = () => {
      const smoothScrollState = smoothScrollRef.current;
      if (smoothScrollState.requestAnimationFrameId) {
        cancelAnimationFrame(smoothScrollState.requestAnimationFrameId);
        smoothScrollState.requestAnimationFrameId = null;
        smoothScrollState.target = null;
      }
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleUserScroll = () => {
      // Clear previous timeout to debounce scroll events
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const scrollContainer = scrollRootRef.current;
        if (!scrollContainer) return;

        const { scrollTop } = scrollContainer;
        // Use a smaller threshold for better responsiveness
        const shouldShowSmallFont = scrollTop > 30;

        // Only update state if it actually changed to avoid unnecessary re-renders
        setHasUserScrolled((prev) => {
          if (prev !== shouldShowSmallFont) {
            return shouldShowSmallFont;
          }
          return prev;
        });
      }, 10); // Small debounce delay
    };

    root.addEventListener('wheel', cancelIfAnimating, { passive: true });
    root.addEventListener('touchstart', cancelIfAnimating, { passive: true });
    root.addEventListener('scroll', handleUserScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      root.removeEventListener('wheel', cancelIfAnimating);
      root.removeEventListener('touchstart', cancelIfAnimating);
      root.removeEventListener('scroll', handleUserScroll);
    };
  }, [stickyIndex]); // Add stickyIndex as dependency

  const messagesWithPhotoDamage = messages as MessageWithPhotoDamage[];
  const firstPhotoDamageAssistantIndex = messagesWithPhotoDamage.findIndex(
    (message) =>
      message.role === 'assistant' &&
      message.photoDamageEstimate &&
      !message.photoDamageEstimate.error,
  );

  const firstPhotoDamageEstimate =
    firstPhotoDamageAssistantIndex !== -1
      ? messagesWithPhotoDamage[firstPhotoDamageAssistantIndex]
          .photoDamageEstimate
      : null;
  const shouldShowRetrySearch =
    loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
    hasImageError;

  // Pair User and Assistant Messages
  const pairs: MessagePair[] = [];
  for (let i = 0; i < messagesWithPhotoDamage.length; i++) {
    if (messagesWithPhotoDamage[i].role === 'user') {
      const userMsg = messagesWithPhotoDamage[i];
      const assistantMsg =
        messagesWithPhotoDamage[i + 1]?.role === 'assistant'
          ? messagesWithPhotoDamage[i + 1]
          : null;
      const assistantIndex = assistantMsg ? i + 1 : null;

      // Skip rendering the pair whose assistant message is rendered
      // as the DamageAnalyzerResultCard to avoid extra vertical spacing.
      if (
        assistantIndex !== null &&
        assistantIndex === firstPhotoDamageAssistantIndex
      ) {
        continue;
      }

      pairs.push({
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex,
      });
    }
  }

  // Scroll to last user message below header
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return;

    const scrollContainer = findScrollContainer(root);
    if (!scrollContainer) return;

    const headerElement = findHeaderElement(
      'Photo Damage Analyzer',
      scrollContainer,
    );
    const lastPair = pairs.length > 0 ? pairs[pairs.length - 1] : null;
    const lastUserElement = lastPair
      ? messageRefs.current[lastPair.userIndex]
      : null;

    const scrollPosition = calculateScrollPosition(
      scrollContainer,
      headerElement,
      lastUserElement,
    );

    scrollContainer.scrollTop = scrollPosition;
    scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
  }, [messages.length, loadingStatus.message, pairs.length]);

  // Calculate the spacer (tailGap) to anchor the last pair to the top
  const adjustScrollGap = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;

    const userHeight = userElement?.getBoundingClientRect().height ?? 0;
    const assistantHeight =
      assistantElement?.getBoundingClientRect().height ?? null;
    const gapHeight = gapRef.current?.getBoundingClientRect().height ?? 0;
    const typingHeight =
      typingRef.current?.getBoundingClientRect().height ?? null;
    const isLoading = loadingStatus.message !== '';
    const maxTailGap = hasImageError ? 0 : MAX_TAIL_GAP;

    const gap = hasImageError
      ? 0
      : calculateTailGap(
          root.clientHeight,
          userHeight,
          assistantHeight,
          gapHeight,
          typingHeight,
          isLoading,
          maxTailGap,
        );

    setTailGap(gap);
  }, [pairs.length, loadingStatus.message, hasImageError]);

  // Sticky header
  useEffect(() => {
    // Clear the previous set of visible assistants
    visibleAssistantsRef.current.clear();

    // Create intersection observer to detect visible messages
    const intersectionObserver = new IntersectionObserver(
      (intersectionEntries) => {
        intersectionEntries.forEach((entry) => {
          const currentAssistantIndex = Number(
            entry.target.getAttribute('data-index'),
          );
          if (Number.isFinite(currentAssistantIndex)) {
            if (entry.isIntersecting) {
              visibleAssistantsRef.current.add(currentAssistantIndex);
            } else {
              visibleAssistantsRef.current.delete(currentAssistantIndex);
            }
          }
        });

        // Calculate the sticky index based on the visible assistants
        const stickyIndex = calculateStickyIndex(
          visibleAssistantsRef.current,
          pairs,
        );
        setStickyIndex(stickyIndex);
      },
      { root: scrollRootRef.current || null, threshold: 0.1 },
    );

    // Observe each assistant message to detect visibility
    pairs.forEach((messagePair) => {
      if (messagePair.assistantIndex !== null) {
        const assistantMessageElement =
          messageRefs.current[messagePair.assistantIndex];
        if (assistantMessageElement) {
          assistantMessageElement.setAttribute(
            'data-index',
            messagePair.assistantIndex.toString(),
          );
          intersectionObserver.observe(assistantMessageElement);
        }
      }
    });

    // Clean up the observer when unmounting
    return () => intersectionObserver.disconnect();
  }, [messages]);

  // ResizeObserver to keep layout consistent
  // This is used to keep the layout consistent when the window is resized
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    // Detect new pair by counting
    prevPairsCountRef.current = pairs.length;

    // Last pair elements already mounted
    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;
    const typingElement =
      lastPair.assistantIndex === null ? typingRef.current : null;

    if (resizeObsRef.current) {
      resizeObsRef.current.disconnect();
    }
    const resizeObserver = new ResizeObserver(() => {
      // Let browser apply layout changes first
      requestAnimationFrame(() => {
        adjustScrollGap();

        // if the last pair still doesn't have an assistant response,
        // reapply the same anchor as for the messages so that user + typing
        // stay in the same visual zone.
        if (lastPair.assistantIndex === null && typingRef.current) {
          const scrollContainer = findScrollContainer(root);
          if (!scrollContainer) {
            return;
          }

          const headerElement = findHeaderElement(
            'Photo Damage Analyzer',
            scrollContainer,
          );

          const anchorElement =
            messageRefs.current[lastPair.userIndex] || typingRef.current;

          const scrollPosition = calculateScrollPosition(
            scrollContainer,
            headerElement,
            anchorElement,
          );

          scrollContainer.scrollTop = scrollPosition;
          scrollContainer.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          });
        }
      });
    });

    resizeObserver.observe(root);
    if (userElement) {
      resizeObserver.observe(userElement);
    }
    if (assistantElement) {
      resizeObserver.observe(assistantElement);
    }
    if (!assistantElement && typingElement) {
      resizeObserver.observe(typingElement);
    }

    resizeObsRef.current = resizeObserver;

    // Initial calculation
    requestAnimationFrame(() => {
      adjustScrollGap();
      lastMeasuredScrollHeight.current = root.scrollHeight;
    });

    return () => resizeObserver.disconnect();
  }, [pairs.length, adjustScrollGap]);

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <MessagesContentStack
      ref={scrollRootRef}
      width="100%"
      spacing={2}
      sx={{
        flexGrow: 1,
      }}
    >
      <DamageAnalyzerContent
        onSendMessage={onSendMessage}
        hasConversationStarted={pairs.length > 0}
        firstPhotoDamageEstimate={firstPhotoDamageEstimate}
        imageToAnalyze={imageToAnalyze}
        setImageToAnalyze={handleSetImageToAnalyze}
        onImageErrorChange={setHasImageError}
        hasImageError={hasImageError}
        onClearImage={handleClearImage}
      />
      {pairs.map((pair, index) => (
        <Stack
          key={index}
          ref={(el) => {
            pairRefs.current[index] = el;
          }}
          width="100%"
          alignItems="center"
          spacing={2}
        >
          {firstPhotoDamageAssistantIndex !== -1 && (
            <MessageBoxContainer
              ref={(el: HTMLDivElement | null) => {
                messageRefs.current[pair.userIndex] = el;
              }}
              sx={baseMessageBoxContainerStyles}
            >
              <MessageContent
                content={pair.user.content}
                isHeader={true}
                isSticky={stickyIndex === pair.userIndex}
                hasUserScrolled={hasUserScrolled}
              />
            </MessageBoxContainer>
          )}
          <AssistantSection
            assistant={pair.assistant}
            assistantIndex={pair.assistantIndex}
            isLastPair={index === pairs.length - 1}
            hasUserScrolled={hasUserScrolled}
            loadingStatus={loadingStatus}
            firstPhotoDamageAssistantIndex={firstPhotoDamageAssistantIndex}
            onMessageRef={(index, element) => {
              messageRefs.current[index] = element;
            }}
            onTypingRef={(element) => {
              typingRef.current = element;
            }}
            onGapRef={(element) => {
              gapRef.current = element;
            }}
            tailGap={tailGap}
          />
        </Stack>
      ))}
      {shouldShowRetrySearch && (
        <Stack width="100%" alignItems="center">
          <RetrySearchInstruction onClearImage={handleClearImage} />
        </Stack>
      )}
    </MessagesContentStack>
  );
};
